import {
  useQuery,
  useMutation,
  type UseQueryOptions,
  type UseMutationOptions,
} from '@tanstack/vue-query';
import axiosInstance from '@/services/api/axios';
import type {
  FetchConfig,
  AxiosErrorResponse,
  ApiQueryOptions,
  ApiMutationOptions,
} from '@/types/api';

const extractAxiosErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosErrorResponse;
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }
    if (axiosError.message) {
      return axiosError.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unknown error occurred';
};

/**
 * A generic API fetching function that handles different HTTP methods and body/query parameters.
 * @returns A promise that resolves to the fetched data.
 */
const fetchAPI = async <T>(config: FetchConfig): Promise<T> => {
  const { endpoint, method = 'get', queryParams, body, headers } = config;

  const axiosConfig: Record<string, unknown> = {
    method,
    url: endpoint,
  };

  if (headers) {
    axiosConfig.headers = { ...headers };
  }

  // Add query parameters
  if (queryParams) {
    axiosConfig.params = queryParams;
  }

  // Add body for POST, PUT, PATCH, DELETE
  if (body !== undefined && method !== 'get') {
    axiosConfig.data = body;
  }

  if (body instanceof FormData) {
    const existingHeaders = (axiosConfig.headers as Record<string, string> | undefined) ?? {};
    axiosConfig.headers = {
      ...existingHeaders,
      'Content-Type': 'multipart/form-data',
    };
  }

  try {
    const response = await axiosInstance(axiosConfig);
    return response.data as T;
  } catch (error) {
    const errorMessage = extractAxiosErrorMessage(error);
    throw new Error(errorMessage);
  }
};

/**
 * A generic composable to fetch data from an API using TanStack Query.
 * @param options The options for the query, including the endpoint and any query parameters.
 * @returns A query object with the fetched data and other query state.
 */
export const useApiQuery = <TQueryFnData, TError = Error, TData = TQueryFnData>(
  options: ApiQueryOptions & UseQueryOptions<TQueryFnData, TError, TData>
) =>
  useQuery<TQueryFnData, TError, TData>({
    ...options,
    queryFn: () =>
      fetchAPI<TQueryFnData>({
        endpoint: options.endpoint,
        method: 'get',
        queryParams: options.queryParams,
        headers: options.headers,
      }),
  });

/**
 * A generic composable to perform data mutations (create, update, delete) using TanStack Query.
 */
export const useApiMutation = <
  TData = unknown,
  TError = Error,
  TVariables = unknown,
  TContext = unknown,
>(
  options: ApiMutationOptions<TVariables> &
    Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'>
) => {
  const { endpoint, method, queryParams, headers, mapVariablesToRequest, ...mutationOptions } =
    options;

  return useMutation<TData, TError, TVariables, TContext>({
    ...mutationOptions,
    mutationFn: async (variables: TVariables) => {
      const dynamicConfig = mapVariablesToRequest?.(variables) ?? {};

      const finalEndpoint = dynamicConfig.endpoint ?? endpoint;
      const finalMethod = dynamicConfig.method ?? method;
      const finalHeaders = (() => {
        const base = headers ? { ...headers } : undefined;
        if (dynamicConfig.headers) {
          return base ? { ...base, ...dynamicConfig.headers } : { ...dynamicConfig.headers };
        }
        return base;
      })();

      const finalQueryParams = (() => {
        const base = queryParams ? { ...queryParams } : undefined;
        if (dynamicConfig.queryParams) {
          return base
            ? { ...base, ...dynamicConfig.queryParams }
            : { ...dynamicConfig.queryParams };
        }
        return base;
      })();

      let body: FetchConfig['body'];
      if ('body' in dynamicConfig) {
        body = dynamicConfig.body;
      } else if (finalMethod !== 'get') {
        body = variables;
      }

      return fetchAPI<TData>({
        endpoint: finalEndpoint,
        method: finalMethod,
        queryParams: finalQueryParams,
        headers: finalHeaders,
        body,
      });
    },
  });
};

// Export a headless helper for non-Vue contexts (e.g., offline sync jobs)
export const apiRequest = fetchAPI;
